import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  componentIncluded,
  validateComponentCore,
  type AssemblyComponentCore,
  type AssemblyInstance,
  type AssemblyTemplate,
  type ComponentInclusion,
  type ComponentRole,
  type ComponentType,
  type QuantityBasis,
} from "../../domain/assembly";
import type { FeatureGeometryBinding, QuantityUnit } from "../../domain/feature";
import type { MeasurementSource } from "../../domain/measurement";
import { computeAssembly, resolveDrivingQuantity, type AssemblyCalculation, type DrivingQuantity } from "../../engine/assembly";
import { ValidationError } from "./projectRepository";
import type { Cents } from "../../domain/money";

/* ------------------------------------------------------------------ */
/* Row types and mapping                                               */
/* ------------------------------------------------------------------ */

interface TemplateRow {
  id: string;
  display_name: string;
  driving_unit: string;
  notes: string;
  is_system: number;
  sort_order: number;
  archived_at: string | null;
}

interface OptionRow {
  id: string;
  prompt: string;
  default_value?: number;
  value?: number;
  sort_order: number;
}

interface ComponentRow {
  id: string;
  instance_id?: string;
  material_definition_id?: string | null;
  default_product_id?: string | null;
  name: string;
  component_type: string;
  role: string;
  inclusion: string;
  condition_option_id: string | null;
  default_included?: number;
  included?: number;
  qty_basis: string;
  fixed_qty_x100: number;
  multiplier_x10000: number;
  waste_pct_x100: number;
  qty_override_x100?: number | null;
  unit: string;
  unit_cost_cents: number;
  labor_minutes_x100?: number | null;
  price_source?: string;
  taxable: number;
  category_id: string | null;
  notes: string;
  sort_order: number;
  archived_at?: string | null;
}

interface InstanceRow {
  id: string;
  project_feature_id: string;
  template_id: string | null;
  name: string;
  driving_unit: string;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapComponentCore(row: ComponentRow, includedValue: boolean): AssemblyComponentCore {
  return {
    id: row.id,
    name: row.name,
    componentType: row.component_type as ComponentType,
    role: row.role as ComponentRole,
    inclusion: row.inclusion as ComponentInclusion,
    conditionOptionId: row.condition_option_id,
    included: includedValue,
    qtyBasis: row.qty_basis as QuantityBasis,
    fixedQtyX100: Number(row.fixed_qty_x100),
    multiplierX10000: Number(row.multiplier_x10000),
    wastePctX100: Number(row.waste_pct_x100),
    unit: row.unit as QuantityUnit,
    unitCostCents: Number(row.unit_cost_cents),
    laborMinutesX100: row.labor_minutes_x100 === null || row.labor_minutes_x100 === undefined ? null : Number(row.labor_minutes_x100),
    taxable: Number(row.taxable) === 1,
    categoryId: row.category_id,
    materialDefinitionId: row.material_definition_id ?? null,
    notes: row.notes,
    sortOrder: Number(row.sort_order),
  };
}

/* ------------------------------------------------------------------ */

export interface TemplateInput {
  displayName: string;
  drivingUnit: QuantityUnit;
  notes: string;
  options: Array<{ id?: string; prompt: string; defaultValue: boolean }>;
  components: Array<Omit<AssemblyComponentCore, "id" | "sortOrder"> & { id?: string; defaultProductId?: string | null }>;
}

export interface ComponentEditInput {
  name: string;
  componentType: ComponentType;
  role: ComponentRole;
  inclusion: ComponentInclusion;
  conditionOptionId: string | null;
  qtyBasis: QuantityBasis;
  fixedQtyX100: number;
  multiplierX10000: number;
  wastePctX100: number;
  unit: QuantityUnit;
  unitCostCents: number;
  laborMinutesX100?: number | null;
  taxable: boolean;
  categoryId: string | null;
  materialDefinitionId?: string | null;
  notes: string;
}

/**
 * Phase 6 hook: product selections and BOM-derived pricing are owned by the
 * catalog layer; the assembly repository asks it (a) to apply template
 * product defaults at attach time and (b) for product-driven component
 * amounts after each regeneration. Kept as an interface so assemblies stay
 * independent of catalog internals.
 */
/** Phase 8.1 estimate-completeness summary for one feature. */
export interface FeatureEstimateStatus {
  featureId: string;
  assemblyNames: string[];
  componentCount: number;
  unpricedCount: number;
  unselectedMaterialCount: number;
}

export interface ProductPricingHook {
  applyTemplateDefault(componentId: string, productId: string): Promise<void>;
  /** Returns product-driven {amount, explanation} per component id for the project, or empty. */
  productDrivenAmounts(projectId: string): Promise<Map<string, { amountCents: number }>>;
}

export class AssemblyRepository {
  private selectionHook: ProductPricingHook | null = null;
  private bulkRegenerationHook: ((projectId: string) => Promise<void>) | null = null;

  constructor(private readonly db: DbExecutor) {}

  setProductPricingHook(hook: ProductPricingHook): void {
    this.selectionHook = hook;
  }

  /**
   * WHERE A WHOLE-PROJECT REGENERATION RUNS (spec §67B).
   *
   * Regenerating one project is hundreds of small statements. On the desktop
   * they are local SQLite calls and cost nothing. In the hosted build every
   * statement is an HTTP round trip — measured at 11 ms against the live
   * beta — so the same work took over two minutes in a browser.
   *
   * The work itself is fine; only its LOCATION was wrong. When this hook is
   * set (the hosted build sets it), the whole regeneration is asked for in
   * one request and runs server-side against the real SQLite file, using
   * this exact same code. Nothing about the result differs.
   */
  setBulkRegenerationHook(hook: (projectId: string) => Promise<void>): void {
    this.bulkRegenerationHook = hook;
  }

  /* ---------------- Templates (library) ---------------- */

  async listTemplates(): Promise<AssemblyTemplate[]> {
    const rows = await this.db.select<TemplateRow>(
      `SELECT id, display_name, driving_unit, notes, is_system, sort_order, archived_at
         FROM assembly_templates ORDER BY sort_order, display_name`,
    );
    const templates: AssemblyTemplate[] = [];
    for (const row of rows) {
      templates.push(await this.hydrateTemplate(row));
    }
    return templates;
  }

  async getTemplate(id: string): Promise<AssemblyTemplate | null> {
    const rows = await this.db.select<TemplateRow>(
      `SELECT id, display_name, driving_unit, notes, is_system, sort_order, archived_at
         FROM assembly_templates WHERE id = ?`,
      [id],
    );
    return rows.length > 0 ? this.hydrateTemplate(rows[0]) : null;
  }

  private async hydrateTemplate(row: TemplateRow): Promise<AssemblyTemplate> {
    const optionRows = await this.db.select<OptionRow>(
      `SELECT id, prompt, default_value, sort_order FROM assembly_template_options
        WHERE template_id = ? ORDER BY sort_order`,
      [row.id],
    );
    const componentRows = await this.db.select<ComponentRow>(
      `SELECT * FROM assembly_template_components WHERE template_id = ? ORDER BY sort_order`,
      [row.id],
    );
    return {
      id: row.id,
      displayName: row.display_name,
      drivingUnit: row.driving_unit as QuantityUnit,
      notes: row.notes,
      isSystem: Number(row.is_system) === 1,
      sortOrder: Number(row.sort_order),
      archivedAt: row.archived_at,
      options: optionRows.map((o) => ({
        id: o.id,
        prompt: o.prompt,
        value: Number(o.default_value) === 1,
        sortOrder: Number(o.sort_order),
      })),
      components: componentRows.map((c) =>
        mapComponentCore(c, Number(c.default_included) === 1),
      ),
      defaultProductIds: Object.fromEntries(componentRows.map((c) => [c.id, c.default_product_id ?? null])),
    };
  }

  /** Create or fully update a template (children replaced atomically-ish).
   *  Instances are copies, so replacing template children is always safe. */
  async saveTemplate(id: string | null, input: TemplateInput): Promise<string> {
    if (input.displayName.trim() === "") {
      throw new ValidationError(["Assembly name is required."]);
    }
    for (const c of input.components) {
      const problems = validateComponentCore(c);
      if (problems.length > 0) throw new ValidationError(problems);
    }
    const now = new Date().toISOString();
    let templateId = id;
    if (templateId === null) {
      templateId = generateId();
      const existing = await this.db.select<{ m: number | null }>(
        "SELECT MAX(sort_order) AS m FROM assembly_templates",
      );
      await this.db.execute(
        `INSERT INTO assembly_templates (id, display_name, driving_unit, notes, is_system, sort_order, created_at, updated_at)
         VALUES (?, ?, ?, ?, 0, ?, ?, ?)`,
        [
          templateId,
          input.displayName.trim(),
          input.drivingUnit,
          input.notes,
          (existing[0]?.m === null ? 0 : Number(existing[0].m)) + 10,
          now,
          now,
        ],
      );
    } else {
      await this.db.execute(
        `UPDATE assembly_templates SET display_name = ?, driving_unit = ?, notes = ?, updated_at = ? WHERE id = ?`,
        [input.displayName.trim(), input.drivingUnit, input.notes, now, templateId],
      );
      await this.db.execute("DELETE FROM assembly_template_components WHERE template_id = ?", [
        templateId,
      ]);
      await this.db.execute("DELETE FROM assembly_template_options WHERE template_id = ?", [
        templateId,
      ]);
    }

    // Options first (components may reference them). Preserve given ids so
    // component conditionOptionId values stay valid.
    const optionIds = new Map<string, string>();
    let sort = 10;
    for (const option of input.options) {
      const optionId = option.id ?? generateId();
      optionIds.set(option.id ?? optionId, optionId);
      await this.db.execute(
        `INSERT INTO assembly_template_options (id, template_id, prompt, default_value, sort_order)
         VALUES (?, ?, ?, ?, ?)`,
        [optionId, templateId, option.prompt, option.defaultValue ? 1 : 0, sort],
      );
      sort += 10;
    }
    sort = 10;
    for (const c of input.components) {
      await this.db.execute(
        `INSERT INTO assembly_template_components
           (id, template_id, name, component_type, role, inclusion, condition_option_id,
            default_included, qty_basis, fixed_qty_x100, multiplier_x10000, waste_pct_x100,
            unit, unit_cost_cents, labor_minutes_x100, taxable, category_id, material_definition_id, default_product_id, notes, sort_order)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          c.id ?? generateId(),
          templateId,
          c.name.trim(),
          c.componentType,
          c.role,
          c.inclusion,
          c.conditionOptionId === null ? null : (optionIds.get(c.conditionOptionId) ?? null),
          c.included ? 1 : 0,
          c.qtyBasis,
          c.fixedQtyX100,
          c.multiplierX10000,
          c.wastePctX100,
          c.unit,
          c.unitCostCents,
          c.laborMinutesX100 ?? null,
          c.taxable ? 1 : 0,
          c.categoryId,
          c.materialDefinitionId ?? null,
          c.defaultProductId ?? null,
          c.notes,
          sort,
        ],
      );
      sort += 10;
    }
    return templateId;
  }

  async archiveTemplate(id: string): Promise<void> {
    await this.db.execute(
      "UPDATE assembly_templates SET archived_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), id],
    );
  }

  async unarchiveTemplate(id: string): Promise<void> {
    await this.db.execute("UPDATE assembly_templates SET archived_at = NULL WHERE id = ?", [id]);
  }

  /* ---------------- Suggestions ---------------- */

  async listSuggestedTemplates(featureDefinitionId: string): Promise<AssemblyTemplate[]> {
    const rows = await this.db.select<{ template_id: string }>(
      `SELECT s.template_id FROM assembly_suggestions s
         JOIN assembly_templates t ON t.id = s.template_id
        WHERE s.feature_definition_id = ? AND t.archived_at IS NULL
        ORDER BY s.sort_order`,
      [featureDefinitionId],
    );
    const templates: AssemblyTemplate[] = [];
    for (const row of rows) {
      const template = await this.getTemplate(row.template_id);
      if (template) templates.push(template);
    }
    return templates;
  }

  async listSuggestionDefinitionIds(templateId: string): Promise<string[]> {
    const rows = await this.db.select<{ feature_definition_id: string }>(
      "SELECT feature_definition_id FROM assembly_suggestions WHERE template_id = ?",
      [templateId],
    );
    return rows.map((r) => r.feature_definition_id);
  }

  async setSuggestions(templateId: string, featureDefinitionIds: string[]): Promise<void> {
    await this.db.execute("DELETE FROM assembly_suggestions WHERE template_id = ?", [templateId]);
    let sort = 10;
    for (const definitionId of featureDefinitionIds) {
      await this.db.execute(
        "INSERT OR IGNORE INTO assembly_suggestions (feature_definition_id, template_id, sort_order) VALUES (?, ?, ?)",
        [definitionId, templateId, sort],
      );
      sort += 10;
    }
  }

  /* ---------------- Instances ---------------- */

  /**
   * Estimate-completeness status per feature (Phase 8.1): powers the
   * "Needs Assembly" / "N of M components unpriced" / "products not
   * selected — generic estimate" feedback so a Design action never
   * silently produces $0. A component counts as priced when it has a
   * manual unit cost OR an active product selection.
   */
  async estimateStatusForFeatures(featureIds: string[]): Promise<Map<string, FeatureEstimateStatus>> {
    const result = new Map<string, FeatureEstimateStatus>();
    if (featureIds.length === 0) return result;
    for (const id of featureIds) result.set(id, { featureId: id, assemblyNames: [], componentCount: 0, unpricedCount: 0, unselectedMaterialCount: 0 });
    const placeholders = featureIds.map(() => "?").join(",");
    const instances = await this.db.select<{ fid: string; name: string; iid: string }>(
      `SELECT project_feature_id AS fid, name, id AS iid FROM assembly_instances
        WHERE project_feature_id IN (${placeholders}) AND archived_at IS NULL ORDER BY created_at`,
      featureIds,
    );
    for (const i of instances) result.get(i.fid)!.assemblyNames.push(i.name);
    if (instances.length === 0) return result;
    const iids = instances.map((i) => i.iid);
    const comps = await this.db.select<{ iid: string; unit_cost_cents: number | null; labor_minutes_x100: number | null; material_definition_id: string | null; sel: number }>(
      `SELECT c.instance_id AS iid, c.unit_cost_cents, c.labor_minutes_x100, c.material_definition_id,
              (SELECT COUNT(*) FROM project_product_selections s WHERE s.component_id = c.id AND s.replaced_at IS NULL) AS sel
         FROM assembly_instance_components c
        WHERE c.instance_id IN (${iids.map(() => "?").join(",")}) AND c.archived_at IS NULL
          AND (c.inclusion = 'required'
            OR (c.inclusion = 'optional' AND c.included = 1)
            OR (c.inclusion = 'conditional' AND EXISTS
              (SELECT 1 FROM assembly_instance_options o WHERE o.id = c.condition_option_id AND o.value = 1)))`,
      iids,
    );
    const featureOf = new Map(instances.map((i) => [i.iid, i.fid]));
    for (const c of comps) {
      const status = result.get(featureOf.get(c.iid)!)!;
      status.componentCount += 1;
      const manualPrice = c.unit_cost_cents !== null && c.unit_cost_cents > 0;
      const durationPriced = c.labor_minutes_x100 !== null && c.labor_minutes_x100 > 0;
      if (!manualPrice && !durationPriced && c.sel === 0) status.unpricedCount += 1;
      if (c.material_definition_id !== null && c.sel === 0) status.unselectedMaterialCount += 1;
    }
    return result;
  }

  async listByFeature(projectFeatureId: string): Promise<AssemblyInstance[]> {
    const rows = await this.db.select<InstanceRow>(
      `SELECT * FROM assembly_instances WHERE project_feature_id = ? AND archived_at IS NULL
        ORDER BY created_at`,
      [projectFeatureId],
    );
    return this.hydrateAll(rows);
  }

  async listByProject(projectId: string): Promise<AssemblyInstance[]> {
    const rows = await this.db.select<InstanceRow>(
      `SELECT i.* FROM assembly_instances i
         JOIN project_features f ON f.id = i.project_feature_id
        WHERE f.project_id = ? AND i.archived_at IS NULL AND f.archived_at IS NULL
        ORDER BY i.created_at`,
      [projectId],
    );
    return this.hydrateAll(rows);
  }

  async getInstance(id: string): Promise<AssemblyInstance | null> {
    const rows = await this.db.select<InstanceRow>(
      "SELECT * FROM assembly_instances WHERE id = ?",
      [id],
    );
    return rows.length > 0 ? this.hydrateInstance(rows[0]) : null;
  }

  /**
   * HYDRATING MANY ASSEMBLIES AT ONCE (spec §67D).
   *
   * `hydrateInstance` costs two queries — its options and its components —
   * which is nothing against a local file and two HTTP round trips in the
   * hosted build. Listing a project's assemblies one at a time therefore
   * cost 2N requests: on a real project, 118 round trips just to read what
   * was already in one table.
   *
   * This reads every option and every component for the whole set in TWO
   * queries and groups them in memory. Same rows, same order, same objects.
   */
  private async hydrateAll(rows: InstanceRow[]): Promise<AssemblyInstance[]> {
    if (rows.length === 0) return [];
    if (rows.length === 1) return [await this.hydrateInstance(rows[0])];

    const ids = rows.map((r) => r.id);
    const placeholders = ids.map(() => "?").join(", ");
    const optionRows = await this.db.select<OptionRow & { instance_id: string }>(
      `SELECT id, instance_id, prompt, value, sort_order FROM assembly_instance_options
        WHERE instance_id IN (${placeholders}) ORDER BY sort_order`,
      ids,
    );
    const componentRows = await this.db.select<ComponentRow & { instance_id: string }>(
      `SELECT * FROM assembly_instance_components WHERE instance_id IN (${placeholders}) ORDER BY sort_order`,
      ids,
    );

    const optionsBy = new Map<string, OptionRow[]>();
    for (const option of optionRows) {
      const list = optionsBy.get(option.instance_id) ?? [];
      list.push(option);
      optionsBy.set(option.instance_id, list);
    }
    const componentsBy = new Map<string, ComponentRow[]>();
    for (const component of componentRows) {
      const list = componentsBy.get(component.instance_id) ?? [];
      list.push(component);
      componentsBy.set(component.instance_id, list);
    }

    return rows.map((row) => this.assembleInstance(row, optionsBy.get(row.id) ?? [], componentsBy.get(row.id) ?? []));
  }

  private async hydrateInstance(row: InstanceRow): Promise<AssemblyInstance> {
    const optionRows = await this.db.select<OptionRow>(
      `SELECT id, prompt, value, sort_order FROM assembly_instance_options
        WHERE instance_id = ? ORDER BY sort_order`,
      [row.id],
    );
    const componentRows = await this.db.select<ComponentRow>(
      `SELECT * FROM assembly_instance_components WHERE instance_id = ? ORDER BY sort_order`,
      [row.id],
    );
    return this.assembleInstance(row, optionRows, componentRows);
  }

  /** One instance, from rows already in hand. The single shape both paths use. */
  private assembleInstance(row: InstanceRow, optionRows: OptionRow[], componentRows: ComponentRow[]): AssemblyInstance {
    return {
      id: row.id,
      projectFeatureId: row.project_feature_id,
      templateId: row.template_id,
      name: row.name,
      drivingUnit: row.driving_unit as QuantityUnit,
      notes: row.notes,
      archivedAt: row.archived_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      options: optionRows.map((o) => ({
        id: o.id,
        prompt: o.prompt,
        value: Number(o.value) === 1,
        sortOrder: Number(o.sort_order),
      })),
      components: componentRows.map((c) => ({
        ...mapComponentCore(c, Number(c.included) === 1),
        instanceId: row.id,
        qtyOverrideX100: c.qty_override_x100 === null ? null : Number(c.qty_override_x100),
        priceSource: c.price_source ?? "manual",
        archivedAt: c.archived_at ?? null,
      })),
    };
  }

  /** Attach a template to a feature: full COPY (options, components, rules,
   *  prices) into a new instance, then generate its lines. */
  async attachTemplate(projectFeatureId: string, templateId: string): Promise<AssemblyInstance> {
    const template = await this.getTemplate(templateId);
    if (!template) {
      throw new ValidationError(["The selected assembly template no longer exists."]);
    }
    const now = new Date().toISOString();
    const instanceId = generateId();
    await this.db.execute(
      `INSERT INTO assembly_instances (id, project_feature_id, template_id, name, driving_unit, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [instanceId, projectFeatureId, templateId, template.displayName, template.drivingUnit, "", now, now],
    );
    const optionIdMap = new Map<string, string>();
    for (const option of template.options) {
      const newId = generateId();
      optionIdMap.set(option.id, newId);
      await this.db.execute(
        `INSERT INTO assembly_instance_options (id, instance_id, prompt, value, sort_order)
         VALUES (?, ?, ?, ?, ?)`,
        [newId, instanceId, option.prompt, option.value ? 1 : 0, option.sortOrder],
      );
    }
    const defaultSelections: Array<{ componentId: string; productId: string }> = [];
    for (const c of template.components) {
      const componentId = generateId();
      await this.db.execute(
        `INSERT INTO assembly_instance_components
           (id, instance_id, name, component_type, role, inclusion, condition_option_id,
            included, qty_basis, fixed_qty_x100, multiplier_x10000, waste_pct_x100,
            unit, unit_cost_cents, labor_minutes_x100, price_source, taxable, category_id, material_definition_id, notes, sort_order,
            created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'manual', ?, ?, ?, ?, ?, ?, ?)`,
        [
          componentId,
          instanceId,
          c.name,
          c.componentType,
          c.role,
          c.inclusion,
          c.conditionOptionId === null ? null : (optionIdMap.get(c.conditionOptionId) ?? null),
          c.included ? 1 : 0,
          c.qtyBasis,
          c.fixedQtyX100,
          c.multiplierX10000,
          c.wastePctX100,
          c.unit,
          c.unitCostCents,
          c.laborMinutesX100 ?? null,
          c.taxable ? 1 : 0,
          c.categoryId,
          c.materialDefinitionId,
          c.notes,
          c.sortOrder,
          now,
          now,
        ],
      );
      const defaultProduct = template.defaultProductIds?.[c.id] ?? null;
      if (defaultProduct) defaultSelections.push({ componentId, productId: defaultProduct });
    }
    // Copy-on-use template product defaults: snapshot now (latest observation
    // if any); later template changes never touch this instance.
    if (this.selectionHook && defaultSelections.length > 0) {
      for (const d of defaultSelections) {
        try {
          await this.selectionHook.applyTemplateDefault(d.componentId, d.productId);
        } catch {
          /* archived/discontinued defaults are simply not applied */
        }
      }
    }
    await this.regenerate(instanceId);
    const instance = await this.getInstance(instanceId);
    if (!instance) throw new Error("Instance disappeared after attach.");
    return instance;
  }

  /** Custom assembly with no template. */
  async createCustomInstance(
    projectFeatureId: string,
    name: string,
    drivingUnit: QuantityUnit,
  ): Promise<AssemblyInstance> {
    if (name.trim() === "") {
      throw new ValidationError(["Assembly name is required."]);
    }
    const now = new Date().toISOString();
    const instanceId = generateId();
    await this.db.execute(
      `INSERT INTO assembly_instances (id, project_feature_id, template_id, name, driving_unit, notes, created_at, updated_at)
       VALUES (?, ?, NULL, ?, ?, '', ?, ?)`,
      [instanceId, projectFeatureId, name.trim(), drivingUnit, now, now],
    );
    const instance = await this.getInstance(instanceId);
    if (!instance) throw new Error("Instance disappeared after create.");
    return instance;
  }

  async setOption(instanceId: string, optionId: string, value: boolean): Promise<void> {
    await this.db.execute("UPDATE assembly_instance_options SET value = ?, automatic_finish_default = NULL WHERE id = ?", [
      value ? 1 : 0,
      optionId,
    ]);
    await this.db.execute(
      "UPDATE assembly_instance_components SET automatic_demolition_default = NULL WHERE condition_option_id = ?",
      [optionId],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
  }

  async setComponentIncluded(instanceId: string, componentId: string, included: boolean): Promise<void> {
    await this.db.execute(
      "UPDATE assembly_instance_components SET included = ?, automatic_demolition_default = NULL, updated_at = ? WHERE id = ?",
      [included ? 1 : 0, new Date().toISOString(), componentId],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
  }

  async setQtyOverride(
    instanceId: string,
    componentId: string,
    qtyOverrideX100: number | null,
  ): Promise<void> {
    if (
      qtyOverrideX100 !== null &&
      (!Number.isSafeInteger(qtyOverrideX100) || qtyOverrideX100 < 0)
    ) {
      throw new ValidationError(["Quantity override must be a valid non-negative number."]);
    }
    await this.db.execute(
      "UPDATE assembly_instance_components SET qty_override_x100 = ?, automatic_demolition_default = NULL, updated_at = ? WHERE id = ?",
      [qtyOverrideX100, new Date().toISOString(), componentId],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
  }

  async updateComponent(
    instanceId: string,
    componentId: string,
    input: ComponentEditInput,
  ): Promise<void> {
    const problems = validateComponentCore(input);
    if (problems.length > 0) throw new ValidationError(problems);
    await this.db.execute(
      `UPDATE assembly_instance_components SET
         name = ?, component_type = ?, role = ?, inclusion = ?, condition_option_id = ?,
         qty_basis = ?, fixed_qty_x100 = ?, multiplier_x10000 = ?, waste_pct_x100 = ?,
         unit = ?, unit_cost_cents = ?, labor_minutes_x100 = ?, taxable = ?, category_id = ?,
         material_definition_id = COALESCE(?, material_definition_id), notes = ?, updated_at = ?, automatic_demolition_default = NULL
       WHERE id = ?`,
      [
        input.name.trim(),
        input.componentType,
        input.role,
        input.inclusion,
        input.conditionOptionId,
        input.qtyBasis,
        input.fixedQtyX100,
        input.multiplierX10000,
        input.wastePctX100,
        input.unit,
        input.unitCostCents,
        input.laborMinutesX100 ?? null,
        input.taxable ? 1 : 0,
        input.categoryId,
        input.materialDefinitionId ?? null,
        input.notes,
        new Date().toISOString(),
        componentId,
      ],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
  }

  async addComponent(instanceId: string, input: ComponentEditInput): Promise<string> {
    const problems = validateComponentCore(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const siblings = await this.db.select<{ m: number | null }>(
      "SELECT MAX(sort_order) AS m FROM assembly_instance_components WHERE instance_id = ?",
      [instanceId],
    );
    const now = new Date().toISOString();
    const componentId = generateId();
    await this.db.execute(
      `INSERT INTO assembly_instance_components
         (id, instance_id, name, component_type, role, inclusion, condition_option_id,
          included, qty_basis, fixed_qty_x100, multiplier_x10000, waste_pct_x100,
          unit, unit_cost_cents, labor_minutes_x100, price_source, taxable, category_id, material_definition_id, notes, sort_order,
          created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?, ?, ?, 'manual', ?, ?, ?, ?, ?, ?, ?)`,
      [
        componentId,
        instanceId,
        input.name.trim(),
        input.componentType,
        input.role,
        input.inclusion,
        input.conditionOptionId,
        input.qtyBasis,
        input.fixedQtyX100,
        input.multiplierX10000,
        input.wastePctX100,
        input.unit,
        input.unitCostCents,
        input.laborMinutesX100 ?? null,
        input.taxable ? 1 : 0,
        input.categoryId,
        input.materialDefinitionId ?? null,
        input.notes,
        (siblings[0]?.m === null ? 0 : Number(siblings[0].m)) + 10,
        now,
        now,
      ],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
    return componentId;
  }

  async addOption(instanceId: string, prompt: string, value: boolean): Promise<string> {
    if (prompt.trim() === "") {
      throw new ValidationError(["Option prompt is required."]);
    }
    const optionId = generateId();
    await this.db.execute(
      `INSERT INTO assembly_instance_options (id, instance_id, prompt, value, sort_order)
       VALUES (?, ?, ?, ?, (SELECT COALESCE(MAX(sort_order),0)+10 FROM assembly_instance_options WHERE instance_id = ?))`,
      [optionId, instanceId, prompt.trim(), value ? 1 : 0, instanceId],
    );
    await this.touchInstance(instanceId);
    return optionId;
  }

  /** Remove (archive) a component; its generated line is archived, never deleted. */
  async archiveComponent(instanceId: string, componentId: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE assembly_instance_components SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [now, now, componentId],
    );
    await this.touchInstance(instanceId);
    await this.regenerate(instanceId);
  }

  /** Detach (archive) an assembly instance; all its generated lines archive. */
  async detachInstance(instanceId: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE assembly_instances SET archived_at = ?, updated_at = ? WHERE id = ?",
      [now, now, instanceId],
    );
    await this.db.execute(
      `UPDATE proposed_cost_lines SET archived_at = ?, updated_at = ?
        WHERE archived_at IS NULL AND assembly_component_id IN
          (SELECT id FROM assembly_instance_components WHERE instance_id = ?)`,
      [now, now, instanceId],
    );
  }

  /** Save a project assembly back to the library as a new template. */
  async saveInstanceAsTemplate(instanceId: string, name: string): Promise<string> {
    const instance = await this.getInstance(instanceId);
    if (!instance) {
      throw new ValidationError(["This assembly no longer exists."]);
    }
    return this.saveTemplate(null, {
      displayName: name,
      drivingUnit: instance.drivingUnit,
      notes: instance.notes,
      options: instance.options.map((o) => ({ id: o.id, prompt: o.prompt, defaultValue: o.value })),
      components: instance.components
        .filter((c) => c.archivedAt === null)
        .map((c) => ({ ...c })),
    });
  }

  private async touchInstance(instanceId: string): Promise<void> {
    await this.db.execute("UPDATE assembly_instances SET updated_at = ? WHERE id = ?", [
      new Date().toISOString(),
      instanceId,
    ]);
  }

  /* ---------------- Generation ---------------- */

  /** The driving quantity comes from the parent feature (spec §21). */
  private async drivingForInstance(instance: AssemblyInstance): Promise<DrivingQuantity> {
    const rows = await this.db.select<{
      quantity_x100: number | null;
      quantity_unit: string | null;
      quantity_source: string | null;
      quantity_geometry_binding: string | null;
      quantity_geometry_x100: number | null;
    }>(
      "SELECT quantity_x100, quantity_unit, quantity_source, quantity_geometry_binding, quantity_geometry_x100 FROM project_features WHERE id = ?",
      [instance.projectFeatureId],
    );
    if (rows.length === 0) {
      throw new ValidationError(["The feature for this assembly no longer exists."]);
    }
    const f = rows[0];
    const resolved = resolveDrivingQuantity(
      {
        quantityX100: f.quantity_x100 === null ? null : Number(f.quantity_x100),
        quantityUnit: f.quantity_unit as QuantityUnit | null,
        quantitySource: f.quantity_source as MeasurementSource | null,
        quantityGeometryBinding: (f.quantity_geometry_binding as FeatureGeometryBinding | null) ?? null,
        quantityGeometryX100: f.quantity_geometry_x100 === null || f.quantity_geometry_x100 === undefined ? null : Number(f.quantity_geometry_x100),
      },
      instance.drivingUnit,
    );
    if (!resolved.ok) throw new ValidationError([resolved.error]);
    return resolved.driving;
  }

  /** Compute without writing (for display/explanations). */
  async calculate(instanceId: string): Promise<AssemblyCalculation> {
    const instance = await this.getInstance(instanceId);
    if (!instance) throw new ValidationError(["This assembly no longer exists."]);
    const driving = await this.drivingForInstance(instance);
    const taxRate = await this.projectTaxRate(instance);
    return computeAssembly(instance, driving, taxRate, await this.laborRate());
  }

  /** Company estimating labor rate (spec §20A) — a LIVE assumption for
   *  current, non-baselined estimates; future baselines freeze it. */
  /**
   * Price a TEMPLATE at a given driving quantity, without instantiating it
   * (Phase 10 package previews). Uses the same engine and the same project
   * tax/labor rates as a real assembly, so a preview never invents its own
   * arithmetic. Returns null when the template cannot consume that unit.
   */
  async estimateTemplate(
    templateId: string,
    drivingQuantityX100: number,
    projectId: string | null,
    unit?: string,
  ): Promise<Cents | null> {
    const template = await this.getTemplate(templateId);
    if (!template || template.archivedAt !== null) return null;
    if (unit !== undefined && unit !== template.drivingUnit) return null;
    const taxRate = projectId === null ? null : await this.projectTaxRateForProject(projectId);
    const calculation = computeAssembly(
      {
        options: template.options,
        components: template.components.map((c) => ({
          ...c,
          instanceId: "",
          qtyOverrideX100: null,
          priceSource: "manual",
          archivedAt: null,
        })),
      },
      { valueX100: drivingQuantityX100, unit: template.drivingUnit, source: "package preview" },
      taxRate,
      await this.laborRate(),
    );
    return calculation.totalCents;
  }

  private async projectTaxRateForProject(projectId: string): Promise<number | null> {
    const rows = await this.db.select<{ estimating_tax_pct_x100: number | null }>(
      "SELECT estimating_tax_pct_x100 FROM projects WHERE id = ?",
      [projectId],
    );
    return rows.length > 0 && rows[0].estimating_tax_pct_x100 !== null ? Number(rows[0].estimating_tax_pct_x100) : null;
  }

  private async laborRate(): Promise<number> {
    const rows = await this.db.select<{ value: string }>(
      "SELECT value FROM app_settings WHERE org_id = 'default' AND key = 'labor_rate_cents_per_hour'",
    );
    const parsed = Number(rows[0]?.value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 2500;
  }

  private async projectTaxRate(instance: AssemblyInstance): Promise<number | null> {
    const rows = await this.db.select<{ estimating_tax_pct_x100: number | null }>(
      `SELECT p.estimating_tax_pct_x100 FROM projects p
         JOIN project_features f ON f.project_id = p.id WHERE f.id = ?`,
      [instance.projectFeatureId],
    );
    return rows.length > 0 && rows[0].estimating_tax_pct_x100 !== null
      ? Number(rows[0].estimating_tax_pct_x100)
      : null;
  }

  /**
   * Regenerate the instance's Proposed Cost Lines with STABLE identity:
   * lines are matched by assembly_component_id and updated in place —
   * never deleted/recreated — so expense links and future baselines
   * survive. Excluded components archive their line; re-included
   * components restore the same line.
   */
  /**
   * PRODUCT PRICING IS PROJECT-WIDE, SO IT RUNS ONCE (spec §67A).
   *
   * `applyProductDrivenAmounts` aggregates every selected product across the
   * WHOLE project before rounding, which is what makes BOM pricing correct.
   * It therefore costs the same whether one assembly changed or fifty — and
   * regenerating a project used to call it once per assembly, recomputing
   * the entire project's BOM `instances` times over. On a real project that
   * was 11,936 SQL statements for 59 assemblies, and in the hosted build
   * every statement is a network round trip.
   *
   * Callers that regenerate MANY instances pass `overlayProducts: false` and
   * run the overlay once when they are done. The result is identical — the
   * overlay is idempotent and depends only on the finished lines.
   */
  async regenerate(
    instanceId: string,
    options: { overlayProducts?: boolean } = {},
  ): Promise<AssemblyCalculation> {
    const instance = await this.getInstance(instanceId);
    if (!instance) throw new ValidationError(["This assembly no longer exists."]);
    if (instance.archivedAt !== null) {
      throw new ValidationError(["This assembly has been detached."]);
    }
    const featureRows = await this.db.select<{ project_id: string }>(
      "SELECT project_id FROM project_features WHERE id = ?",
      [instance.projectFeatureId],
    );
    const projectId = featureRows[0]?.project_id;
    const driving = await this.drivingForInstance(instance);
    const taxRate = await this.projectTaxRate(instance);
    const calculation = computeAssembly(instance, driving, taxRate, await this.laborRate());
    const now = new Date().toISOString();

    for (const calc of calculation.components) {
      const component = instance.components.find((c) => c.id === calc.componentId);
      if (!component) continue;
      const existing = await this.db.select<{
        id: string;
        amount_cents: number;
        description: string;
        category_id: string | null;
        cost_type: string | null;
        archived_at: string | null;
      }>(
        `SELECT id, amount_cents, description, category_id, cost_type, archived_at
           FROM proposed_cost_lines WHERE assembly_component_id = ?`,
        [calc.componentId],
      );

      if (calc.included) {
        if (existing.length === 0) {
          await this.db.execute(
            `INSERT INTO proposed_cost_lines
               (id, project_id, description, amount_cents, category_id, cost_type,
                feature_id, assembly_component_id, source, sort_order, notes, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'assembly', ?, '', ?, ?)`,
            [
              generateId(),
              projectId,
              component.name,
              calc.amountCents,
              component.categoryId,
              component.componentType,
              instance.projectFeatureId,
              calc.componentId,
              component.sortOrder,
              now,
              now,
            ],
          );
        } else {
          const line = existing[0];
          const changed =
            Number(line.amount_cents) !== calc.amountCents ||
            line.description !== component.name ||
            line.category_id !== component.categoryId ||
            line.cost_type !== component.componentType ||
            line.archived_at !== null;
          if (changed) {
            await this.db.execute(
              `UPDATE proposed_cost_lines SET
                 description = ?, amount_cents = ?, category_id = ?, cost_type = ?,
                 archived_at = NULL, updated_at = ?
               WHERE id = ?`,
              [component.name, calc.amountCents, component.categoryId, component.componentType, now, line.id],
            );
          }
        }
      } else if (existing.length > 0 && existing[0].archived_at === null) {
        await this.db.execute(
          "UPDATE proposed_cost_lines SET archived_at = ?, updated_at = ? WHERE id = ?",
          [now, now, existing[0].id],
        );
      }
    }
    // Phase 6: product-driven pricing is project-wide (aggregate-then-round
    // across instances), so overlay BOM-derived amounts for every component
    // in the project that has a product selection. Same line UUIDs, amounts
    // updated in place.
    if (this.selectionHook && projectId && options.overlayProducts !== false) {
      await this.applyProductDrivenAmounts(projectId);
    }
    return calculation;
  }

  /** Overlay BOM-derived amounts onto generated lines of selected components. */
  async applyProductDrivenAmounts(projectId: string): Promise<void> {
    if (!this.selectionHook) return;
    const amounts = await this.selectionHook.productDrivenAmounts(projectId);
    if (amounts.size === 0) return;
    const now = new Date().toISOString();
    for (const [componentId, { amountCents }] of amounts) {
      await this.db.execute(
        `UPDATE proposed_cost_lines SET amount_cents = ?, updated_at = ?
          WHERE assembly_component_id = ? AND archived_at IS NULL AND amount_cents <> ?`,
        [amountCents, now, componentId, amountCents],
      );
    }
  }

  /** Regenerate every active assembly on a feature (e.g. after its
   *  quantity changed). Silently skips instances whose driving quantity
   *  is currently unresolvable — the UI surfaces those when configuring. */
  async regenerateForFeature(projectFeatureId: string): Promise<void> {
    const instances = await this.listByFeature(projectFeatureId);
    let projectId: string | null = null;
    for (const instance of instances) {
      try {
        await this.regenerate(instance.id, { overlayProducts: false });
      } catch (err) {
        if (!(err instanceof ValidationError)) throw err;
      }
    }
    if (this.selectionHook && instances.length > 0) {
      const rows = await this.db.select<{ project_id: string }>(
        "SELECT project_id FROM project_features WHERE id = ?",
        [projectFeatureId],
      );
      projectId = rows[0]?.project_id ?? null;
      if (projectId) await this.applyProductDrivenAmounts(projectId);
    }
  }

  /** Self-healing reconcile for a whole project (idempotent; no-ops when
   *  nothing changed). */
  async regenerateForProject(projectId: string): Promise<void> {
    if (this.bulkRegenerationHook) {
      await this.bulkRegenerationHook(projectId);
      return;
    }
    await this.regenerateForProjectLocally(projectId);
  }

  /** The regeneration itself, wherever it happens to be running. */
  async regenerateForProjectLocally(projectId: string): Promise<void> {
    const instances = await this.listByProject(projectId);
    for (const instance of instances) {
      try {
        await this.regenerate(instance.id, { overlayProducts: false });
      } catch (err) {
        if (!(err instanceof ValidationError)) throw err;
      }
    }
    // Once, at the end — not once per assembly (spec §67A).
    if (this.selectionHook && instances.length > 0) await this.applyProductDrivenAmounts(projectId);
  }

  /** Project id owning an instance component (for product selection flows). */
  async projectIdForComponent(componentId: string): Promise<string | null> {
    const rows = await this.db.select<{ project_id: string }>(
      `SELECT f.project_id FROM assembly_instance_components c
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN project_features f ON f.id = i.project_feature_id
        WHERE c.id = ?`,
      [componentId],
    );
    return rows[0]?.project_id ?? null;
  }
}

export { componentIncluded };
